import React from "react";
import { useSelector } from "react-redux";
import { updateSlideTemplates, saveCurrentPageTemplates } from "../../store/storyline/actions";
import { UnControlled as CodeMirror } from "react-codemirror2"
import "codemirror/mode/jsx/jsx";
import "codemirror/mode/css/css";
import "codemirror/addon/fold/foldcode";
import "codemirror/addon/fold/foldgutter";
import "codemirror/addon/fold/brace-fold";
import "codemirror/addon/fold/xml-fold";
import "codemirror/addon/fold/comment-fold";
import "codemirror/addon/fold/markdown-fold";
import "codemirror/addon/fold/foldgutter.css";
import "codemirror/lib/codemirror.css";
import "codemirror/theme/material.css";
import { Button, TabPanel, Tab, Tabs, Toolbar } from "../../shared/components";
import { RootState, useThunkDispatch } from "../../store";
import { TemplateDefinition } from "../../store/storyline/types";


function shortenTemplateName(templateName: string) {
    return templateName.match(/((?:[^\/]+\/){0,1}[^\/]+)$/)?.[1] ?? templateName;
}

function Templates() {
    const templates = useSelector((root: RootState) => root.storyline?.storyline?.templates ?? {});
    const currentPageTemplate = useSelector((root: RootState) => root.storyline.currentParagraph?.templateName);
    const [selectedTabValue, setSelectedTabValue] = React.useState(currentPageTemplate);

    const handleTabChange = React.useCallback((_event: React.ChangeEvent<{}>, newValue: string) => {
        setSelectedTabValue(newValue);
    }, [setSelectedTabValue]);

    return (
        <div className="tabs-container">
            <Tabs
                variant="scrollable"
                visibleScrollbar
                scrollButtons={false}
                style={{ backgroundColor: "var(--colors-blue-1)" }}
                value={selectedTabValue}
                onChange={handleTabChange}
            >
                {
                    Object.keys(templates).map(templateName => <Tab label={shortenTemplateName(templateName)} value={templateName} wrapped title={templateName} />)
                }
            </Tabs>

            {
                Object.keys(templates).map((templateName) =>
                    <TabPanel key={templateName} selectedValue={selectedTabValue} value={templateName}>
                        <Template key={templateName} templateName={templateName} templates={templates} />
                    </TabPanel>
                )
            }
        </div>
    );
};

interface TemplateProps {
    templateName: string,
    templates: Record<string, TemplateDefinition>
}

const JSX_EDITOR_OPTIONS = {
    mode: "text/jsx",
    lineNumbers: true,
    indentUnit: 4,
    theme: "material",
    foldGutter: true,
    gutters: ["CodeMirror-linenumbers", "CodeMirror-foldgutter"]
};

const CSS_EDITOR_OPTIONS = {
    mode: "text/css",
    lineNumbers: true,
    indentUnit: 4,
    theme: "material",
    foldGutter: true,
    gutters: ["CodeMirror-linenumbers", "CodeMirror-foldgutter"]
};

function Template(props: TemplateProps) {
    const { templateName, templates } = props;
    const template = templates[templateName] ?? { jsx: "", css: "" };
    const jsxEditorRef = React.useRef(null);
    const cssEditorRef = React.useRef(null);
    const dispatch = useThunkDispatch();

    const jsxEditorDidMount = React.useCallback((editor) => {
        jsxEditorRef.current = editor;
        setTimeout(() => editor.execCommand("goDocStart"));
    }, []);
    const cssEditorDidMount = React.useCallback((editor) => {
        cssEditorRef.current = editor;
        setTimeout(() => editor.execCommand("goDocStart"));
    }, []);

    const applyTemplateChange = React.useCallback(() => {
        dispatch(updateSlideTemplates({ [templateName]: { jsx: jsxEditorRef.current.doc.getValue(), css: cssEditorRef.current.doc.getValue() } }));
    }, [templateName]);

    const saveTemplate = React.useCallback(() => {
        dispatch(updateSlideTemplates({ [templateName]: { jsx: jsxEditorRef.current.doc.getValue(), css: cssEditorRef.current.doc.getValue() } }));
        dispatch(saveCurrentPageTemplates({ ...templates, [templateName]: { jsx: jsxEditorRef.current.doc.getValue(), css: cssEditorRef.current.doc.getValue() } }));
    }, [templateName, templates]);

    return (
        <div className="template-panel">
            <CodeMirror
                value={template.jsx}
                options={JSX_EDITOR_OPTIONS}
                editorDidMount={jsxEditorDidMount}
            />
            <div className="css-heading">CSS</div>
            <CodeMirror
                value={template.css || "// Custom CSS goes here..."}
                options={CSS_EDITOR_OPTIONS}
                editorDidMount={cssEditorDidMount}
            />

            <Toolbar className="d-flex flex-row align-items-center justify-content-center gap-2">
                <Button color="secondary" variant="contained" onClick={applyTemplateChange}>Apply Changes</Button>
                <Button color="success" variant="contained" className="save-changes" onClick={saveTemplate}>Save Template</Button>
            </Toolbar>
        </div >
    );

}

export default React.memo(Templates);